import { getAuth } from 'firebase/auth';

export interface EnsureStripeCustomerResult {
    orgId: string;
    stripeCustomerId: string;
    created: boolean;
    email?: string | null;
    name?: string | null;
}

const stripeBase = () => import.meta.env.VITE_GAMUT_STRIPE_URL?.replace(/\/$/, '') ?? '';

/**
 * Calls the stripeBilling Cloud Function to create a Stripe Customer for the org (idempotent)
 * and persist ids on `billing/{orgId}` and `organizations/{orgId}`. No-op if `VITE_GAMUT_STRIPE_URL` is unset.
 */
export async function ensureStripeCustomerForOrg(orgId: string): Promise<EnsureStripeCustomerResult | null> {
    const base = stripeBase();
    if (!base) {
        return null;
    }

    const user = getAuth().currentUser;
    if (!user) {
        throw new Error('Not signed in');
    }

    const token = await user.getIdToken();
    const r = await fetch(`${base}/api/billing/ensure-stripe-customer`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ orgId }),
    });

    const data = (await r.json()) as Record<string, unknown>;
    if (!r.ok) {
        const err = typeof data.error === 'string' ? data.error : r.statusText;
        throw new Error(err);
    }

    const stripeCustomerId = data.stripeCustomerId;
    if (typeof stripeCustomerId !== 'string' || !stripeCustomerId) {
        throw new Error('Invalid response from billing service');
    }

    return {
        orgId: typeof data.orgId === 'string' ? data.orgId : orgId,
        stripeCustomerId,
        created: data.created === true,
        email: typeof data.email === 'string' ? data.email : undefined,
        name: typeof data.name === 'string' ? data.name : undefined,
    };
}
